import { COLORS } from './styles';
import type { ExcelWriteContext } from './types';

/**
 * Apply the pre-resolved numFmt and horizontal alignment of a column
 * to a single ExcelJS cell. Must be called AFTER `row.addRow(...)` for
 * every data / subtotal / grand-total row — the column-level style set
 * via `sheet.columns[i].style` doesn't reliably propagate to cells
 * created through `addRow(object)`, so each cell gets its style stamped
 * individually here.
 *
 * Kept as a tiny helper so the callers stay readable and the styling
 * logic lives in exactly one place.
 */
export function applyCellFormat<T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cell: any,
  columnId: string,
  ctx: ExcelWriteContext<T>
): void {
  const fmt = ctx.formatByColId.get(columnId);
  if (!fmt) return;
  if (fmt.numFmt) cell.numFmt = fmt.numFmt;
  cell.alignment = {
    ...(cell.alignment ?? {}),
    horizontal: fmt.align,
    vertical: 'middle',
  };
}

/**
 * Apply the "slot" fill (`dataType: 'slot'`, ex: US-C attendance matrices)
 * to a single cell — solid green when `isPresent`, solid red when
 * `isAbsent` (and not present), left untouched otherwise. "Present" always
 * wins over "absent" — same precedence as the on-screen `slotColumn`
 * renderer (a factual clock-in trace overrides a declared absence period).
 * Kept separate from `applyCellFormat` because this is a cell-level fill
 * driven by booleans (`col.slotValue` / `col.slotAbsentValue`), not a
 * numFmt/alignment rule, and only data rows have per-row booleans to test
 * (subtotal / group-header rows never call this).
 */
export function applySlotFill(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cell: any,
  { isPresent, isAbsent }: { isPresent: boolean; isAbsent: boolean }
): void {
  if (!isPresent && !isAbsent) return;
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: isPresent ? COLORS.slotFill : COLORS.slotAbsentFill },
  };
}
